// Dependencies
import React, { FC } from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { Box, CircularProgress } from '@mui/material';

export const ApexLineChart: FC<{
  id?: string,
  data: any,
  isSolActive?: boolean,
  color?: string,
  loading?: boolean,
  showToolbar?: boolean,
  fillType?: 'gradient' | 'solid',
  /** 'smooth' keeps the ApexCharts default; 'straight' connects data points linearly. */
  curve?: 'smooth' | 'straight',
  /** Line thickness in px. ApexCharts defaults to 2. */
  strokeWidth?: number,
  /** 'area' keeps the filled default; 'line' renders the plain curve. */
  chartType?: 'area' | 'line',
  /**
   * 'datetime' keeps the default time-proportional axis. 'category' spaces the points
   * evenly, so days without a quote (weekends, holidays) cannot appear as plateaus —
   * the standard for financial charts. Requires xAxisCategories.
   */
  xAxisType?: 'datetime' | 'category',
  /** One label per data point; empty strings render no tick. Only for xAxisType 'category'. */
  xAxisCategories?: string[],
  /** Full date per data point for the tooltip — categories are mostly blank by design. */
  xAxisTooltipLabels?: string[],
  fillOpacityFrom?: number,
  fillOpacityTo?: number,
  fillShadeIntensity?: number,
  solidFillOpacity?: number,
  height?: number,
  background?: string,
  seriesName?: string,
  showLegend?: boolean,
  yAxisPrefix?: string,
  yAxisFormatter?: (value: number) => string,
  /** Force Y-axis minimum (e.g. 0 for percent-from-zero charts). */
  yAxisMin?: number,
  yAxisMax?: number,
  /**
   * Number of Y-axis intervals. Opt-in: without it ApexCharts derives its own count,
   * which on a forced min/max can land on unreadable steps.
   */
  yAxisTickAmount?: number,
  /** Shift Y-axis labels horizontally (px); negative moves them toward the page edge. */
  yAxisLabelOffsetX?: number,
  /**
   * Padding around the plot area (px). ApexCharts' default leaves a generous top and
   * bottom gap; a print chart with a forced axis does not need it.
   */
  gridPadding?: { top?: number; right?: number; bottom?: number; left?: number },
  /** Axis tick label size (px). */
  axisLabelFontSize?: string | number,
  axisLabelColor?: string,
}> = ({
  id,
  data,
  isSolActive,
  color,
  loading,
  showToolbar = true,
  fillType = 'gradient',
  curve = 'smooth',
  strokeWidth = 2,
  chartType = 'area',
  xAxisType = 'datetime',
  xAxisCategories,
  xAxisTooltipLabels,
  fillOpacityFrom = 0.7,
  fillOpacityTo = 0.9,
  fillShadeIntensity = 1,
  solidFillOpacity = 0.25,
  height = 350,
  background,
  seriesName = '',
  showLegend,
  yAxisPrefix = '€',
  yAxisFormatter,
  yAxisMin,
  yAxisMax,
  yAxisTickAmount,
  yAxisLabelOffsetX,
  gridPadding,
  axisLabelFontSize,
  axisLabelColor,
}) => {
  const axisLabelStyle = {
    ...(axisLabelFontSize != null
      ? { fontSize: String(axisLabelFontSize).endsWith('px') ? String(axisLabelFontSize) : `${axisLabelFontSize}px` }
      : null),
    ...(axisLabelColor ? { colors: axisLabelColor } : null),
  };
// BarChart option
  const options: ApexOptions = {
    chart: {
      id: id || 'area-datetime',
      type: chartType,
      height,
      background,
      zoom: {
        autoScaleYaxis: true,
      },
      toolbar: {
        show: showToolbar,
      },
    },
    legend: {
      show: showLegend ?? !!seriesName,
      showForSingleSeries: true,
      showForNullSeries: true,
      showForZeroSeries: true,
      position: 'bottom',
    },
    annotations: {
      yaxis: [],
      // Placeholder annotation without an x value. The datetime axis silently drops it;
      // on a category axis it would render a stray "Rally" flag at index 0.
      xaxis:
        xAxisType === 'category'
          ? []
          : [
              {
                borderColor: color || '#003F7A',
                label: {
                  text: 'Rally',
                  style: {
                    color: '#fff',
                    background: color || '#003F7A',
                  },
                },
              },
            ],
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve,
      width: strokeWidth,
    },
    // Prevent stray "hover dots" from being rendered/captured when the user hovers the chart.
    // (Apex can still show a hover marker even when base marker size is 0.)
    markers: {
      size: 0,
      hover: { size: 0, sizeOffset: 0 },
    },
    xaxis:
      xAxisType === 'category'
        ? {
            type: 'category',
            categories: xAxisCategories || [],
            tickPlacement: 'on',
            axisTicks: { show: false },
            labels: {
              style: axisLabelStyle,
              rotate: 0,
              hideOverlappingLabels: true,
              trim: false,
            },
          }
        : {
            type: 'datetime',
            tickAmount: 6,
            labels: {
              style: axisLabelStyle,
            },
          },
    yaxis: {
      show: true,
      ...(yAxisMin !== undefined ? { min: yAxisMin } : {}),
      ...(yAxisMax !== undefined ? { max: yAxisMax } : {}),
      ...(yAxisTickAmount !== undefined ? { tickAmount: yAxisTickAmount } : {}),
      labels: {
        style: axisLabelStyle,
        ...(yAxisLabelOffsetX !== undefined ? { offsetX: yAxisLabelOffsetX } : {}),
        formatter: (value) => {
          if (yAxisFormatter) return yAxisFormatter(value);
          const rounded = Math.round(value);
          const formatted = Number.isFinite(rounded) ? rounded.toLocaleString('de-DE') : String(value);
          return `${yAxisPrefix || ''}${formatted}`;
        },
      },
    },
    tooltip:
      xAxisType === 'category' && xAxisTooltipLabels
        ? {
            x: {
              formatter: (_val, opts) =>
                xAxisTooltipLabels[opts?.dataPointIndex ?? -1] || '',
            },
          }
        : {
            x: {
              format: 'MMM dd yyyy',
            },
          },
    ...(gridPadding ? { grid: { padding: gridPadding } } : {}),
    colors: [color || '#003F7A'],
    fill:
      fillType === 'solid'
        ? {
            type: 'solid',
            opacity: solidFillOpacity,
          }
        : fillType === 'gradient' ? {
            type: 'gradient',
            gradient: {
              shadeIntensity: fillShadeIntensity,
              opacityFrom: fillOpacityFrom,
              opacityTo: fillOpacityTo,
              stops: [0, 100],
            },
          } : {
            type: 'sold',
            colors: ['#ffffff'],
          },
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#00000008'
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <ReactApexChart
        options={options}
        series={[
          {
            name: seriesName,
            data: isSolActive ? data : data?.map((history: any) => [history.timestamp, history.value]) || [],
          },
        ]}
        type={chartType}
        height={height}
      />
    </Box>
  );
};

export const ApexPieChart: FC<{
  id?: string;
  data: Array<{ label: string; value: number }>;
  colors?: string[];
  height?: number;
  showLegend?: boolean;
  background?: string;
  showDataLabels?: boolean;
  additionalOptions?: Partial<ApexOptions>;
}> = ({ id, data, colors, height = 320, showLegend = true, background, showDataLabels = true, additionalOptions }) => {
  const defaultColors = ['#1c4f92', '#62418b', '#853874', '#9d345c', '#a63f43', '#a0512c'];

  const options: ApexOptions = {
    chart: {
      id: id || 'pie-chart',
      type: 'pie',
      background,
      toolbar: { show: false },
    },
    labels: data.map((d) => d.label),
    legend: { show: showLegend, position: 'right' },
    dataLabels: {
      enabled: showDataLabels,
      formatter: (val: number) => `${Math.round(val)}%`,
      style: { fontWeight: 700 },
    },
    colors: colors || defaultColors,
    stroke: { width: 1, colors: ['#fff'] },
    ...additionalOptions
  };

  const series = data.map((d) => d.value);

  return (
    <Box>
      <ReactApexChart
        key={JSON.stringify(data)}
        height={height}
        options={options}
        series={series}
        type="pie"
      />
    </Box>
  );
};
